import { useCallback } from "react";
import {
	DndContext,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
	type DragEndEvent,
} from "@dnd-kit/core";
import {
	SortableContext,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import type { CompactSelectOption } from "newui/components/ui/InputWithLabel";

import type { ColumnConfig } from "../../SpreadsheetEditor";
import type { ScopeItemRowDraft } from "../ScopeItemRowsEditor";
import { GhostAddRow } from "../../SpreadsheetEditor";
import { TemplateRow } from "./TemplateRow";

export interface TemplateRowsBodyProps {
	columns: ColumnConfig[];
	rows: ScopeItemRowDraft[];
	unitOptions: CompactSelectOption[];
	/** Rows with violations (e.g. missing UoM when the column is mandatory). */
	rowsWithUnitError?: Set<number>;
	onAddRow: () => void;
	onChangeRow: (
		rowId: number,
		field: "name" | "unitId",
		value: string | number | null
	) => void;
	onRemoveRow: (rowId: number) => void;
	onReorderRows: (sourceIndex: number, destIndex: number) => void;
}

/**
 * Body renderer for `SpreadsheetGrid` inside the template editor.
 *
 * Each row aligns with the configured columns:
 *  - The FIRST `description` column hosts the Item Name input (-> row.name).
 *  - The FIRST `unit_of_measurement` column hosts the unit dropdown
 *    (-> row.unitId).
 *  - All other column cell types are bidder-fill — rendered as greyed
 *    sample text, since the template data model (name + unit_id only)
 *    cannot persist procurer pre-fills for those types.
 *
 * Rows are vertically reorderable via @dnd-kit. A trailing ghost
 * "+ Add row" tr is appended.
 */
export const TemplateRowsBody: React.FC<TemplateRowsBodyProps> = ({
	columns,
	rows,
	unitOptions,
	rowsWithUnitError,
	onAddRow,
	onChangeRow,
	onRemoveRow,
	onReorderRows,
}) => {
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
	);

	const nameColumnId =
		columns.find((c) => c.columnType === "description")?.localId ?? null;
	const unitColumnId =
		columns.find((c) => c.columnType === "unit_of_measurement")?.localId ??
		null;

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event;
			if (!over || active.id === over.id) return;
			const sourceIndex = rows.findIndex((r) => String(r.id) === active.id);
			const destIndex = rows.findIndex((r) => String(r.id) === over.id);
			if (sourceIndex < 0 || destIndex < 0) return;
			onReorderRows(sourceIndex, destIndex);
		},
		[rows, onReorderRows]
	);

	const sortableIds = rows.map((r) => String(r.id));
	// Header column count: leading sticky # + N columns + trailing ghost-add.
	const trailingColSpan = columns.length + 2;

	return (
		<>
			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragEnd={handleDragEnd}
			>
				<SortableContext
					items={sortableIds}
					strategy={verticalListSortingStrategy}
				>
					{rows.map((row, rowIndex) => (
						<TemplateRow
							key={row.id}
							row={row}
							rowIndex={rowIndex}
							columns={columns}
							nameColumnId={nameColumnId}
							unitColumnId={unitColumnId}
							unitOptions={unitOptions}
							unitHasError={rowsWithUnitError?.has(row.id) ?? false}
							onChangeRow={onChangeRow}
							onRemoveRow={onRemoveRow}
						/>
					))}
				</SortableContext>
			</DndContext>

			<GhostAddRow
				colSpan={trailingColSpan}
				onAdd={onAddRow}
				data-testid="template-ghost-add-row"
			/>
		</>
	);
};

export default TemplateRowsBody;
